const lazyAutoplayVideos = new WeakSet();
const lazyAutoplayVideoState = new WeakMap();
let lazyAutoplayObserver = null;

function injectLoaderStyles() {
  if (document.getElementById('lazy-autoplay-video-loader-styles')) return;

  const style = document.createElement('style');
  style.id = 'lazy-autoplay-video-loader-styles';
  style.textContent = `
    .lazy-autoplay-video__loading {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 26px;
      height: 26px;
      margin: -13px 0 0 -13px;
      z-index: 3;
      pointer-events: none;
    }

    .lazy-autoplay-video__loading::before {
      content: "";
      display: block;
      width: 100%;
      height: 100%;
      border: 2px solid rgba(255, 255, 255, 0.75);
      border-top-color: rgba(255, 255, 255, 0.2);
      border-radius: 50%;
      filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.35));
      animation: lazyAutoplayVideoSpin 0.75s linear infinite;
    }

    .lazy-autoplay-video__loading[hidden] {
      display: none;
    }

    @keyframes lazyAutoplayVideoSpin {
      to {
        transform: rotate(360deg);
      }
    }
  `;
  document.head.appendChild(style);
}

function getVideoState(video) {
  let state = lazyAutoplayVideoState.get(video);
  if (!state) {
    state = {
      isNearViewport: false,
      retryTimeout: null,
      retryAttempts: 0,
      loader: null
    };
    lazyAutoplayVideoState.set(video, state);
  }

  return state;
}

function ensureLoader(video) {
  const state = getVideoState(video);
  if (state.loader) return state.loader;

  const parent = video.parentElement;
  if (!parent) return null;

  injectLoaderStyles();

  if (getComputedStyle(parent).position === 'static') {
    parent.style.position = 'relative';
  }

  const loader = document.createElement('span');
  loader.className = 'lazy-autoplay-video__loading';
  loader.hidden = true;
  loader.setAttribute('aria-hidden', 'true');
  video.insertAdjacentElement('afterend', loader);
  state.loader = loader;

  return loader;
}

function showLoader(video) {
  const loader = ensureLoader(video);
  if (loader) loader.hidden = false;
}

function hideLoader(video) {
  const state = getVideoState(video);
  if (state.loader) state.loader.hidden = true;
}

function playVideo(video) {
  if (!video.isConnected) return;

  video.muted = true;
  video.setAttribute('muted', '');
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  video.preload = 'metadata';

  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
    showLoader(video);
  }

  if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) {
    video.load();
  }

  const playPromise = video.play();
  if (playPromise?.catch) {
    playPromise.then(() => {
      getVideoState(video).retryAttempts = 0;
      hideLoader(video);
    }).catch(() => {
      scheduleRetry(video);
    });
  }
}

function prepareVideo(video) {
  video.autoplay = false;
  video.removeAttribute('autoplay');
  video.preload = 'none';
  video.playsInline = true;
  video.setAttribute('playsinline', '');
}

function syncVideo(video, isNearViewport) {
  const state = getVideoState(video);
  state.isNearViewport = isNearViewport;

  if (isNearViewport) {
    playVideo(video);
    return;
  }

  video.pause();
  hideLoader(video);
}

function scheduleRetry(video) {
  const state = getVideoState(video);
  if (!state.isNearViewport || state.retryTimeout || state.retryAttempts >= 6) return;

  state.retryAttempts += 1;
  state.retryTimeout = window.setTimeout(() => {
    state.retryTimeout = null;
    if (state.isNearViewport && video.paused) playVideo(video);
  }, 300);
}

function bindVideoReadiness(video) {
  const state = getVideoState(video);
  if (state.eventsBound) return;

  state.eventsBound = true;

  const retryWhenReady = () => {
    const currentState = getVideoState(video);
    if (currentState.isNearViewport && video.paused) playVideo(video);
  };

  const showLoadingIfNeeded = () => {
    const currentState = getVideoState(video);
    if (currentState.isNearViewport && video.paused) showLoader(video);
  };

  ['loadstart', 'waiting', 'stalled'].forEach((eventName) => {
    video.addEventListener(eventName, showLoadingIfNeeded);
  });

  ['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough'].forEach((eventName) => {
    video.addEventListener(eventName, retryWhenReady);
  });

  video.addEventListener('playing', () => {
    getVideoState(video).retryAttempts = 0;
    hideLoader(video);
  });
}

function getObserver() {
  if (lazyAutoplayObserver || typeof IntersectionObserver === 'undefined') {
    return lazyAutoplayObserver;
  }

  lazyAutoplayObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      syncVideo(entry.target, entry.isIntersecting);
    });
  }, {
    rootMargin: '700px 0px',
    threshold: 0.01
  });

  return lazyAutoplayObserver;
}

function initLazyAutoplayVideos(root = document) {
  root.querySelectorAll('video.lazy-autoplay-video').forEach((video) => {
    if (lazyAutoplayVideos.has(video)) return;

    lazyAutoplayVideos.add(video);
    prepareVideo(video);
    bindVideoReadiness(video);

    const observer = getObserver();
    if (observer) {
      observer.observe(video);
    } else {
      playVideo(video);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initLazyAutoplayVideos());
} else {
  initLazyAutoplayVideos();
}

document.addEventListener('shopify:section:load', (event) => initLazyAutoplayVideos(event.target));
document.addEventListener('theme:section:load', (event) => initLazyAutoplayVideos(event.target || document));
